import { useMutation } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import { useCallback, useState } from 'react';
import type { AssetAlias, AssetAliasesResponse } from 'types/api-responses';
import { aliasErrorMessage, trimAliasName } from './utils';

const ADD_FAILED_MESSAGE = "Couldn't add the alias. Try again.";

interface UseAssetAliasesOptions {
  /** The record's alias endpoint, e.g. `/crm/tracts/a00f4…/aliases`. Removing adds `/:id`. */
  aliasesPath: string;
  /** Called with the record's full alias list after an add or a remove. */
  onChange: (aliases: AssetAlias[]) => void;
}

/**
 * Strips the leading slash: the ky `api` client has a prefixUrl, so paths must not start with one.
 * @param {string} path - An endpoint path.
 * @return {string} The path ky accepts.
 */
const toPath = (path: string) => path.replace(/^\/+/, '');

/**
 * Adds and removes a record's aliases. Both endpoints answer with the full updated list, which goes to
 * `onChange`, so the caller never has to fetch the record again.
 * @param {UseAssetAliasesOptions} options - The record's alias endpoint and the list callback.
 * @return {object} `add`, which resolves to the error to show or null; `isAdding`; `remove`; the ids
 *   being removed; and the last removal's error.
 */
export function useAssetAliases({
  aliasesPath,
  onChange,
}: UseAssetAliasesOptions) {
  const path = toPath(aliasesPath);
  const addMutation = useMutation({
    mutationFn: (name: string) =>
      api
        .post(path, { json: { name: trimAliasName(name) } })
        .json<AssetAliasesResponse>(),
  });
  const removeMutation = useMutation({
    mutationFn: (alias: { id: number }) =>
      api.delete(`${path}/${alias.id}`).json<AssetAliasesResponse>(),
  });
  const [removingIds, setRemovingIds] = useState<ReadonlySet<number>>(
    new Set()
  );
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { mutateAsync: sendAdd } = addMutation;
  const add = useCallback(
    async (name: string): Promise<string | null> => {
      try {
        const response = await sendAdd(name);
        onChange(response.aliases);
        return null;
      } catch (error) {
        return aliasErrorMessage(error, ADD_FAILED_MESSAGE);
      }
    },
    [onChange, sendAdd]
  );

  const { mutateAsync: sendRemove } = removeMutation;
  const remove = useCallback(
    async (alias: { id: number; name: string }) => {
      setRemoveError(null);
      setRemovingIds((ids) => new Set(ids).add(alias.id));
      try {
        const response = await sendRemove(alias);
        onChange(response.aliases);
      } catch {
        setRemoveError(
          `Couldn't remove alias ${alias.name}. Refresh the page and try again.`
        );
      } finally {
        setRemovingIds((ids) => {
          const next = new Set(ids);
          next.delete(alias.id);
          return next;
        });
      }
    },
    [onChange, sendRemove]
  );

  return {
    add,
    isAdding: addMutation.isPending,
    remove,
    removingIds,
    removeError,
  };
}
